import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  Box,
  Button,
  Divider,
} from "@mui/material";

export default function PricingAdviceDialog({ summaryId, open, onClose }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [advice, setAdvice] = useState(null);
  const [riskReview, setRiskReview] = useState(null);
  const [riskReviewLoading, setRiskReviewLoading] = useState(false);
  const [riskReviewGenerating, setRiskReviewGenerating] = useState(false);
  const [riskReviewError, setRiskReviewError] = useState(null);
  const [riskReviewSuccess, setRiskReviewSuccess] = useState(null);
  const [lotReviewClassification, setLotReviewClassification] = useState(null);
  const [lotReviewClassificationLoading, setLotReviewClassificationLoading] = useState(false);
  const [lotReviewClassificationError, setLotReviewClassificationError] = useState(null);

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      setAdvice(null);
      setError(null);
      setLoading(false);
      setRiskReview(null);
      setRiskReviewLoading(false);
      setRiskReviewGenerating(false);
      setRiskReviewError(null);
      setRiskReviewSuccess(null);
      setLotReviewClassification(null);
      setLotReviewClassificationLoading(false);
      setLotReviewClassificationError(null);
    }
  }, [open]);

  // Fetch fresh advice on open
  useEffect(() => {
    if (open && summaryId) {
      fetchAdvice();
      fetchLatestRiskReview();
      fetchLotReviewClassification();
    }
  }, [open, summaryId]);

  const fetchAdvice = async () => {
    setLoading(true);
    setError(null);
    setAdvice(null);
    try {
      const res = await fetch(`/api/pricing-advice/by-summary/${summaryId}`);
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || "Failed to load pricing advice");
      setAdvice(json);
      console.log("✅ Latest pricing advice:", json);
    } catch (err) {
      console.error(err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchLatestRiskReview = async () => {
    setRiskReviewLoading(true);
    setRiskReviewError(null);
    setRiskReviewSuccess(null);
    try {
      const res = await fetch(`/api/llm-pricing-reviews/summary/${summaryId}/latest`);
      const json = await res.json();
      if (res.status === 404) {
        setRiskReview(null);
        return;
      }
      if (!res.ok) throw new Error(json.error || "Failed to load pricing risk review");
      setRiskReview(json.review || null);
    } catch (err) {
      console.error(err);
      setRiskReviewError(err.message);
    } finally {
      setRiskReviewLoading(false);
    }
  };

  const fetchLotReviewClassification = async () => {
    setLotReviewClassificationLoading(true);
    setLotReviewClassificationError(null);
    try {
      const res = await fetch(`/api/llm-lot-reviews/summary/${summaryId}/classification`);
      const json = await res.json();
      if (res.status === 404) {
        setLotReviewClassification(null);
        return;
      }
      if (!res.ok) throw new Error(json.error || "Failed to load lot review classification");
      setLotReviewClassification(json || null);
    } catch (err) {
      console.error(err);
      setLotReviewClassificationError(err.message);
    } finally {
      setLotReviewClassificationLoading(false);
    }
  };

  const handleGenerateRiskReview = async () => {
    setRiskReviewGenerating(true);
    setRiskReviewError(null);
    setRiskReviewSuccess(null);
    try {
      const res = await fetch(`/api/llm-pricing-reviews/summary/${summaryId}/generate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ created_by: "pricing_advice_dialog" }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || "Failed to generate pricing risk review");
      setRiskReview(json.review || null);
      setRiskReviewSuccess("Pricing risk review generated.");
    } catch (err) {
      console.error(err);
      setRiskReviewError(err.message);
    } finally {
      setRiskReviewGenerating(false);
    }
  };

  const renderProfitChip = (flag) => {
    return (
      <Chip
        label={flag ? "✅ Profitable to Source" : "❌ Not Profitable"}
        color={flag ? "success" : "error"}
        variant="outlined"
        size="small"
        sx={{ ml: 1 }}
      />
    );
  };

  const renderRiskChip = (riskLevel) => {
    const normalizedRisk = String(riskLevel || "unknown").toLowerCase();
    const color =
      normalizedRisk === "high"
        ? "error"
        : normalizedRisk === "medium"
        ? "warning"
        : normalizedRisk === "low"
        ? "success"
        : "default";

    return (
      <Chip
        label={`Risk: ${normalizedRisk}`}
        color={color}
        variant="filled"
        size="small"
      />
    );
  };

  const formatRiskReviewCost = (cost) => {
    if (cost === null || cost === undefined || cost === "") return "N/A";
    const numericCost = Number(cost);
    return Number.isFinite(numericCost) ? `$${numericCost.toFixed(4)}` : String(cost);
  };

  const formatRiskReviewMoney = (value) => {
    if (value === null || value === undefined || value === "") return "N/A";
    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? `$${numericValue.toFixed(2)}` : String(value);
  };

  const hasRiskReviewMetadata = (review) => {
    return Boolean(review?.total_tokens || review?.cost_usd || review?.llm_model || review?.model);
  };

  const hasLotReadyRiskSnapshot = (review) => {
    return Boolean(
      review?.lot_review_signal ||
        review?.primary_risk_type ||
        review?.offer_risk_level ||
        review?.liquidity_risk_level ||
        review?.active_market_risk_level ||
        review?.identity_risk_level ||
        review?.price_volatility_risk_level ||
        review?.recommended_adjusted_sale_price ||
        review?.max_safe_offer_price
    );
  };

  const lotReviewClassificationColor = (classification) => {
    if (classification === "top_value") return "success";
    if (classification === "risky") return "error";
    if (classification === "manual_review") return "warning";
    return "default";
  };

  const renderLotReviewClassificationPanel = () => {
    const classifications = lotReviewClassification?.classifications || [];

    return (
      <Box
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 1,
          p: 1.5,
          bgcolor: "background.paper",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
          <Typography variant="subtitle2">Lot Review Classification</Typography>
          <Chip label="Advisory only" color="info" variant="outlined" size="small" />
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Lot review classification is advisory only.
        </Typography>

        {lotReviewClassificationLoading && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <CircularProgress size={16} />
            <Typography variant="body2">Loading lot review classification...</Typography>
          </Box>
        )}

        {lotReviewClassificationError && (
          <Alert severity="warning" sx={{ mt: 1 }}>
            {lotReviewClassificationError}
          </Alert>
        )}

        {!lotReviewClassificationLoading && !lotReviewClassificationError && classifications.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            No latest lot review classification found for this card.
          </Typography>
        )}

        {classifications.length > 0 && (
          <Box sx={{ display: "grid", gap: 1 }}>
            {classifications.map((item, index) => (
              <Box key={`${item.classification || "classification"}-${index}`}>
                <Chip
                  label={item.label || item.classification}
                  color={lotReviewClassificationColor(item.classification)}
                  size="small"
                  sx={{ mr: 1, mb: 0.5 }}
                />
                {item.suggested_action && (
                  <Chip label={`Action: ${item.suggested_action}`} variant="outlined" size="small" sx={{ mb: 0.5 }} />
                )}
                <Typography variant="body2">
                  <strong>Reason:</strong> {item.reason || "No reason provided."}
                </Typography>
              </Box>
            ))}
            {lotReviewClassification?.latest_lot_review?.created_at && (
              <Typography variant="caption" color="text.secondary">
                Source lot review: {lotReviewClassification.latest_lot_review.created_at}
              </Typography>
            )}
          </Box>
        )}
      </Box>
    );
  };

  const normalizeRecommendedExclusions = (review) => {
    const exclusions = review?.recommended_exclusions_json;
    if (Array.isArray(exclusions)) return exclusions;
    if (typeof exclusions === "string" && exclusions.trim()) {
      try {
        const parsed = JSON.parse(exclusions);
        return Array.isArray(parsed) ? parsed : [];
      } catch (err) {
        console.warn("Unable to parse recommended exclusions", err);
      }
    }
    return [];
  };

  const renderRiskReviewPanel = () => {
    const recommendedExclusions = normalizeRecommendedExclusions(riskReview);

    return (
      <Box mt={3}>
        <Divider sx={{ mb: 2 }} />
        <Typography variant="subtitle1" gutterBottom>
          <strong>LLM Pricing Risk Review</strong>
        </Typography>
        <Alert severity="info" sx={{ mb: 2 }}>
          This review is advisory only. It does not automatically exclude sales or change pricing advice.
        </Alert>
        <Button
          variant="contained"
          onClick={handleGenerateRiskReview}
          disabled={!summaryId || riskReviewGenerating}
          sx={{ mb: 2 }}
        >
          {riskReviewGenerating ? "Reviewing Pricing Risk..." : "Review Pricing Risk"}
        </Button>
        {riskReviewLoading && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <CircularProgress size={18} />
            <Typography variant="body2">Loading latest pricing risk review...</Typography>
          </Box>
        )}
        {riskReviewSuccess && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {riskReviewSuccess}
          </Alert>
        )}
        {riskReviewError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {riskReviewError}
          </Alert>
        )}
        {renderLotReviewClassificationPanel()}
        {!riskReviewLoading && !riskReview ? (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            No pricing risk review has been generated for this summary yet.
          </Typography>
        ) : null}
        {riskReview && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              {renderRiskChip(riskReview.risk_level)}
              <Chip
                label={
                  riskReview.manual_review_required
                    ? "Manual review required"
                    : "Manual review not required"
                }
                color={riskReview.manual_review_required ? "warning" : "success"}
                variant="filled"
                size="small"
              />
              {riskReview.buyer_action && (
                <Chip
                  label={`Buyer Action: ${riskReview.buyer_action}`}
                  variant="outlined"
                  size="small"
                />
              )}
            </Box>

            {hasLotReadyRiskSnapshot(riskReview) && (
              <Box
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1,
                  p: 1.5,
                  bgcolor: "background.paper",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                  <Typography variant="subtitle2">Lot-Ready Risk Snapshot</Typography>
                  <Chip label="Advice only" color="info" variant="outlined" size="small" />
                </Box>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 1 }}>
                  {riskReview.lot_review_signal && (
                    <Chip label={`Lot Signal: ${riskReview.lot_review_signal}`} size="small" />
                  )}
                  {riskReview.primary_risk_type && (
                    <Chip label={`Primary Risk: ${riskReview.primary_risk_type}`} variant="outlined" size="small" />
                  )}
                  {riskReview.offer_risk_level && (
                    <Chip label={`Offer: ${riskReview.offer_risk_level}`} variant="outlined" size="small" />
                  )}
                  {riskReview.liquidity_risk_level && (
                    <Chip label={`Liquidity: ${riskReview.liquidity_risk_level}`} variant="outlined" size="small" />
                  )}
                  {riskReview.active_market_risk_level && (
                    <Chip label={`Active Market: ${riskReview.active_market_risk_level}`} variant="outlined" size="small" />
                  )}
                  {riskReview.identity_risk_level && (
                    <Chip label={`Identity: ${riskReview.identity_risk_level}`} variant="outlined" size="small" />
                  )}
                  {riskReview.price_volatility_risk_level && (
                    <Chip label={`Volatility: ${riskReview.price_volatility_risk_level}`} variant="outlined" size="small" />
                  )}
                </Box>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))",
                    gap: 1,
                  }}
                >
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Adjusted Sale Price
                    </Typography>
                    <Typography variant="body2">
                      {formatRiskReviewMoney(riskReview.recommended_adjusted_sale_price)}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Max Safe Offer
                    </Typography>
                    <Typography variant="body2">
                      {formatRiskReviewMoney(riskReview.max_safe_offer_price)}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            )}

            <Box
              sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
                p: 1.5,
                bgcolor: "background.paper",
              }}
            >
              <Typography variant="subtitle2" gutterBottom>
                Review Summary
              </Typography>
              <Typography variant="body2" gutterBottom>
                {riskReview.review_summary || "N/A"}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                <strong>Outlier Notes:</strong> {riskReview.outlier_notes || "N/A"}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Recommended Price Basis:</strong>{" "}
                {riskReview.recommended_price_basis || "N/A"}
              </Typography>
              {riskReview.created_at && (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                  Generated At: {riskReview.created_at}
                </Typography>
              )}
            </Box>

            <Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                <Typography variant="subtitle2">
                  Recommended Exclusions
                </Typography>
                <Chip label="Advice only" color="info" variant="outlined" size="small" />
              </Box>
              {recommendedExclusions.length > 0 ? (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  {recommendedExclusions.map((item, index) => (
                    <Box
                      key={`${item.sales_data_id || "row"}-${index}`}
                      sx={{
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 1,
                        p: 1.25,
                        bgcolor: "background.paper",
                      }}
                    >
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 0.5 }}>
                        <Chip
                          label={`Sales Row ID: ${item.sales_data_id || "N/A"}`}
                          variant="outlined"
                          size="small"
                        />
                        <Chip label="Not applied" color="warning" variant="outlined" size="small" />
                      </Box>
                      <Typography variant="body2">
                        <strong>Reason:</strong> {item.reason || "N/A"}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No recommended exclusions returned.
                </Typography>
              )}
            </Box>

            {hasRiskReviewMetadata(riskReview) && (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))",
                  gap: 1,
                }}
              >
                <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, p: 1 }}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Total Tokens
                  </Typography>
                  <Typography variant="body2">{riskReview.total_tokens || "N/A"}</Typography>
                </Box>
                <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, p: 1 }}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Cost USD
                  </Typography>
                  <Typography variant="body2">{formatRiskReviewCost(riskReview.cost_usd)}</Typography>
                </Box>
                <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, p: 1 }}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Model
                  </Typography>
                  <Typography variant="body2">{riskReview.llm_model || riskReview.model || "N/A"}</Typography>
                </Box>
              </Box>
            )}
          </Box>
        )}
      </Box>
    );
  };

  // Helper to decide if we have any elite data worth showing
  const hasEliteData =
    advice &&
    (advice.elite_predicted_sale_price != null ||
      advice.elite_suggested_buy_price != null ||
      advice.elite_margin_percent != null ||
      advice.elite_confidence != null ||
      advice.elite_sourcing_score != null ||
      advice.elite_profitable_to_source != null);

  // Helper for new 30/90/merged fields
  const hasWindowBreakdown =
    advice &&
    (advice.calculated_price_90 != null ||
      advice.suggested_buy_price_90 != null ||
      advice.merged_calculated_price != null ||
      advice.merged_suggested_buy_price != null);

  // Helper for active listings stats
  const hasActiveStats =
    advice &&
    (advice.active_low_price_no_shipping != null ||
      advice.active_median_price_no_shipping != null ||
      advice.active_average_price_no_shipping != null ||
      advice.active_listings_count != null);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Pricing Advice</DialogTitle>
      <DialogContent>
        {loading ? (
          <CircularProgress />
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : advice ? (
          <>
            {/* Existing (Basic) model */}
            <Typography gutterBottom>
              <strong>Method:</strong> {advice.price_method ?? "N/A"}
            </Typography>
            <Typography gutterBottom>
              <strong>Avg Price (primary window):</strong> $
              {Number(advice.calculated_price ?? 0).toFixed(2)}
            </Typography>
            <Typography gutterBottom>
              <strong>Suggested Buy (primary window):</strong> $
              {Number(advice.suggested_buy_price ?? 0).toFixed(2)}
            </Typography>
            <Typography gutterBottom>
              <strong>Margin %:</strong> {advice.margin_percent ?? "N/A"}%
            </Typography>
            <Typography gutterBottom>
              <strong>Confidence Score:</strong>{" "}
              {advice.confidence_score ?? "N/A"}
            </Typography>
            <Typography gutterBottom>
              <strong>Sales Count:</strong> {advice.sales_count ?? "N/A"}
            </Typography>
            <Typography gutterBottom>
              <strong>Spread:</strong> $
              {Number(advice.sales_spread ?? 0).toFixed(2)}
            </Typography>
            <Typography gutterBottom>
              <strong>Sourcing Score:</strong>{" "}
              {advice.sourcing_score ?? "N/A"}%
              {renderProfitChip(!!advice.profitable_to_source)}
            </Typography>

            {/* 30-day / 90-day / merged breakdown (v2 data) */}
            {hasWindowBreakdown && (
              <Box mt={3}>
                <Typography variant="subtitle1" gutterBottom>
                  <strong>Window Breakdown (30-day vs 90-day)</strong>
                </Typography>
                <Typography gutterBottom>
                  <strong>Calculated Price (90-day):</strong>{" "}
                  {advice.calculated_price_90 != null
                    ? `$${Number(advice.calculated_price_90).toFixed(2)}`
                    : "N/A"}
                </Typography>
                <Typography gutterBottom>
                  <strong>Suggested Buy (90-day):</strong>{" "}
                  {advice.suggested_buy_price_90 != null
                    ? `$${Number(advice.suggested_buy_price_90).toFixed(2)}`
                    : "N/A"}
                </Typography>
                <Typography gutterBottom>
                  <strong>Merged Calculated Price:</strong>{" "}
                  {advice.merged_calculated_price != null
                    ? `$${Number(advice.merged_calculated_price).toFixed(2)}`
                    : "N/A"}
                </Typography>
                <Typography gutterBottom>
                  <strong>Merged Suggested Buy:</strong>{" "}
                  {advice.merged_suggested_buy_price != null
                    ? `$${Number(advice.merged_suggested_buy_price).toFixed(2)}`
                    : "N/A"}
                </Typography>
              </Box>
            )}

            {/* Active listings stats (min / median / avg / count + sell-through) */}
            {hasActiveStats && (
              <Box mt={3}>
                <Typography variant="subtitle1" gutterBottom>
                  <strong>Active Listings</strong>
                </Typography>
                <Typography gutterBottom>
                  <strong>Active Listings Count:</strong>{" "}
                  {advice.active_listings_count != null
                    ? advice.active_listings_count
                    : "N/A"}
                </Typography>
                <Typography gutterBottom>
                  <strong>Low Active Price (no shipping):</strong>{" "}
                  {advice.active_low_price_no_shipping != null
                    ? `$${Number(
                        advice.active_low_price_no_shipping
                      ).toFixed(2)}`
                    : "N/A"}
                </Typography>
                <Typography gutterBottom>
                  <strong>Median Active Price (no shipping):</strong>{" "}
                  {advice.active_median_price_no_shipping != null
                    ? `$${Number(
                        advice.active_median_price_no_shipping
                      ).toFixed(2)}`
                    : "N/A"}
                </Typography>
                <Typography gutterBottom>
                  <strong>Average Active Price (no shipping):</strong>{" "}
                  {advice.active_average_price_no_shipping != null
                    ? `$${Number(
                        advice.active_average_price_no_shipping
                      ).toFixed(2)}`
                    : "N/A"}
                </Typography>
                {advice.active_listings_count > 0 &&
                  advice.sales_count != null && (
                    <Typography gutterBottom>
                      <strong>Sell-through Rate (sold / active):</strong>{" "}
                      {`${(
                        (Number(advice.sales_count) /
                          Number(advice.active_listings_count)) *
                        100
                      ).toFixed(1)}%`}
                    </Typography>
                  )}
              </Box>
            )}

            {/* Elite model (v2 – now backed by merged price) */}
            {hasEliteData && (
              <Box mt={3}>
                <Typography variant="subtitle1" gutterBottom>
                  <strong>Elite Model</strong>
                </Typography>
                <Typography gutterBottom>
                  <strong>Elite Predicted Price:</strong> $
                  {advice.elite_predicted_sale_price != null
                    ? Number(advice.elite_predicted_sale_price).toFixed(2)
                    : "N/A"}
                </Typography>
                <Typography gutterBottom>
                  <strong>Elite Suggested Buy:</strong> $
                  {advice.elite_suggested_buy_price != null
                    ? Number(advice.elite_suggested_buy_price).toFixed(2)
                    : "N/A"}
                </Typography>
                <Typography gutterBottom>
                  <strong>Elite Margin %:</strong>{" "}
                  {advice.elite_margin_percent != null
                    ? `${advice.elite_margin_percent}%`
                    : "N/A"}
                </Typography>
                <Typography gutterBottom>
                  <strong>Elite Confidence:</strong>{" "}
                  {advice.elite_confidence != null
                    ? advice.elite_confidence
                    : "N/A"}
                </Typography>
                <Typography gutterBottom>
                  <strong>Elite Sourcing Score:</strong>{" "}
                  {advice.elite_sourcing_score != null
                    ? `${advice.elite_sourcing_score}%`
                    : "N/A"}
                  {renderProfitChip(!!advice.elite_profitable_to_source)}
                </Typography>
              </Box>
            )}
          </>
        ) : (
          <Typography>No pricing advice available.</Typography>
        )}
        {summaryId && renderRiskReviewPanel()}
      </DialogContent>
    </Dialog>
  );
}
